/**
 * RemoveStretchedClusterDialog (titled "Change Stretched Cluster")
 *
 * Confirmation dialog for removing stretched cluster assignment from one or more VMs.
 * Clears the `pf9_failure_domain` metadata key while preserving all other metadata.
 * Shows guidance on how to move a VM to the peer stretched cluster.
 *
 * @module RemoveStretchedClusterDialog
 * @see RemoveStretchedClusterDialog.spec.md
 */
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import ShowActionState, { ActionStateResult } from 'core/components/ShowActionState'
import Button from 'core/elements/button'
import Modal from 'core/elements/modal'
import Text from 'core/elements/Text'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { STRETCHED_CLUSTER_METADATA_KEY } from '../infrastructure/stretched-clusters/constants'
import { updateVmMetadata } from './actions'
import { IVirtualMachinesSelector } from './newModel'
import { stretchedClusterByHostIdSelector, stretchedClustersByClusterSelector } from './selectors'

interface Props {
  onClose: (success?: boolean) => void
  rows: IVirtualMachinesSelector[]
}

/**
 * Modal dialog explaining stretched cluster change process for VMs.
 * Removes the pf9_failure_domain metadata key on confirmation.
 */
export default function RemoveStretchedClusterDialog({ onClose, rows }: Props) {
  const stretchedClustersByCluster = useSelector(stretchedClustersByClusterSelector)
  const stretchedClusterByHostId = useSelector(stretchedClusterByHostIdSelector)
  const { update, bulkUpdate, updating, reset } = useUpdateAction(updateVmMetadata)
  const [bulkResults, setBulkResults] = useState<ActionStateResult[]>([])

  const isBulk = rows?.length > 1
  const firstVm = rows?.[0]

  const hostId = firstVm?.['OS-EXT-SRV-ATTR:host'] || ''
  const hostName = firstVm?.['OS-EXT-SRV-ATTR:hypervisor_hostname'] || hostId
  const hostDomain = stretchedClusterByHostId[hostId]

  const clusterName =
    firstVm?.pinned_availability_zone || firstVm?.['OS-EXT-AZ:availability_zone'] || ''
  const pair = stretchedClustersByCluster[clusterName]

  const peerDomain = useMemo(() => {
    if (!pair || !hostDomain) return null
    if (String(pair.domainA?.id) === String(hostDomain.id)) return pair.domainB
    if (String(pair.domainB?.id) === String(hostDomain.id)) return pair.domainA
    return null
  }, [pair, hostDomain])

  const stripFdMetadata = useCallback((vm: IVirtualMachinesSelector) => {
    const existing = { ...(vm?.metadata ?? {}) }
    delete existing[STRETCHED_CLUSTER_METADATA_KEY]
    return existing
  }, [])

  const handleClose = useCallback(
    (success?: boolean) => {
      reset()
      setBulkResults([])
      onClose(success)
    },
    [reset, onClose],
  )

  const handleSubmit = useCallback(async () => {
    if (isBulk) {
      const initialResults: ActionStateResult[] = rows.map((vm) => ({
        name: vm?.name ?? vm?.id,
        status: 'In Progress' as const,
        actionName: 'Removing',
      }))
      setBulkResults(initialResults)

      const items = rows.map((vm) => ({
        id: vm.id,
        body: { metadata: stripFdMetadata(vm) },
      }))

      const results = await bulkUpdate(items)
      const finalResults: ActionStateResult[] = results.map((res, i) => ({
        name: rows[i]?.name ?? rows[i]?.id,
        status: (res?.success ? 'Success' : 'Failed') as ActionStateResult['status'],
        actionName: res?.success ? 'Removed' : 'Failed to remove',
        error: res?.success ? undefined : res?.error?.message,
      }))
      setBulkResults(finalResults)

      if (results.every((r) => r?.success)) {
        handleClose(true)
      }
    } else {
      const body = { metadata: stripFdMetadata(firstVm) }
      const { success } = await update({ id: firstVm.id, body })
      if (success) {
        handleClose(true)
      }
    }
  }, [rows, firstVm, update, bulkUpdate, isBulk, stripFdMetadata, handleClose])

  const showFinishButton = bulkResults.some((r) => !!r?.status)

  return (
    <Modal
      panel="dialog"
      open
      onClose={() => handleClose()}
      title="Change Stretched Cluster"
      maxWidth={600}
      footer={
        showFinishButton ? (
          <Button variant="secondary" onClick={() => handleClose(true)}>
            Finish
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancel
            </Button>
            <Button disabled={updating} onClick={handleSubmit} loading={updating}>
              Remove Stretched Cluster
            </Button>
          </>
        )
      }
    >
      {bulkResults.length > 0 ? (
        <ShowActionState
          title="Removing stretched cluster from the following VMs:"
          results={bulkResults}
        />
      ) : isBulk ? (
        <>
          <Text variant="body2">
            Remove stretched cluster assignment from <strong>{rows.length} selected VMs</strong>?
          </Text>
          <Text variant="body2" color="textSecondary">
            A VM always belongs to its host&apos;s stretched cluster.
          </Text>
        </>
      ) : (
        <>
          <Text variant="body2">
            <strong>{firstVm?.name ?? firstVm?.id}</strong> has soft affinity to{' '}
            <strong>{firstVm?.stretchedClusterName || hostDomain?.name}</strong>, the stretched
            cluster of its host <strong>{hostName}</strong>. A VM always belongs to its host&apos;s
            stretched cluster.
          </Text>
          {peerDomain && (
            <Text variant="body2" style={{ marginTop: 16 }}>
              To move it to the peer domain <strong>{peerDomain.name}</strong>:
              <ol style={{ margin: '8px 0', paddingLeft: 24 }}>
                <li>Remove its stretched cluster.</li>
                <li>
                  Live-migrate it to a host in <strong>{peerDomain.name}</strong>.
                </li>
                <li>
                  Assign it to <strong>{peerDomain.name}</strong>.
                </li>
              </ol>
            </Text>
          )}
        </>
      )}
    </Modal>
  )
}
